import { ChevronRight } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import type { PromoCode } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { CardHeading, CardStat, CardStats, DesktopTable, MobileCardList, mobileCardClass } from '@/shared/ui/responsive-table.tsx'
import { expiryText, promoCodePath, promoTerms, STATUS_LABELS, STATUS_TONES, usageText } from '../labels.ts'
import { CopyCodeButton } from './copy-code-button.tsx'

const COLUMNS = ['Код', 'Что даёт', 'Ввели', 'Действует', 'Статус', 'Создал']

function StatusBadge({ promo }: { promo: PromoCode }) {
  return <Badge tone={STATUS_TONES[promo.status]}>{STATUS_LABELS[promo.status]}</Badge>
}

function PromoCard({ promo }: { promo: PromoCode }) {
  return (
    <li className="relative">
      <Link
        to={promoCodePath(promo.id)}
        className={cn(mobileCardClass, 'flex-row items-center pr-14 transition-colors active:bg-surface-hover')}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <CardHeading aside={<StatusBadge promo={promo} />}>
            <div className="truncate font-mono text-[15px] font-semibold tracking-[0.03em]">{promo.code}</div>
          </CardHeading>
          <div className="text-[14px] font-medium">{promoTerms(promo)}</div>
          <CardStats>
            <CardStat label="Ввели">
              <span className="tabular">{usageText(promo)}</span>
            </CardStat>
            <CardStat label="Действует">{expiryText(promo)}</CardStat>
          </CardStats>
          {promo.note && <div className="truncate text-[12.5px] text-ink-muted">{promo.note}</div>}
        </div>
        <ChevronRight className="size-4 shrink-0 text-ink-faint" aria-hidden />
      </Link>
      {/* Outside the link: copying must not open the card. */}
      <div className="absolute top-3 right-2">
        <CopyCodeButton code={promo.code} compact />
      </div>
    </li>
  )
}

export function PromoCodesList({ rows }: { rows: PromoCode[] }) {
  const navigate = useNavigate()

  return (
    <>
      <MobileCardList label="Промокоды">
        {rows.map((promo) => (
          <PromoCard key={promo.id} promo={promo} />
        ))}
      </MobileCardList>

      <DesktopTable>
        <table className="w-full min-w-[820px] text-left text-[13.5px]">
          <thead className="border-b border-line-soft text-[12px] text-ink-muted">
            <tr>
              {COLUMNS.map((title) => (
                <th key={title} scope="col" className="px-3.5 py-3 font-medium whitespace-nowrap first:pl-5">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft align-middle">
            {rows.map((promo) => (
              <tr
                key={promo.id}
                onClick={() => navigate(promoCodePath(promo.id))}
                className={cn('cursor-pointer hover:bg-surface-hover/50', promo.status !== 'active' && 'text-ink-muted')}
              >
                <td className="py-2 pr-3.5 pl-5">
                  <div className="flex items-center gap-1">
                    <Link
                      to={promoCodePath(promo.id)}
                      onClick={(event) => event.stopPropagation()}
                      className="font-mono font-semibold tracking-[0.03em] text-ink hover:text-brand"
                    >
                      {promo.code}
                    </Link>
                    <CopyCodeButton code={promo.code} compact />
                  </div>
                  {promo.note && (
                    <div className="max-w-[220px] truncate text-[12px] text-ink-muted" title={promo.note}>
                      {promo.note}
                    </div>
                  )}
                </td>
                <td className="px-3.5 py-3 font-medium">{promoTerms(promo)}</td>
                <td className="tabular px-3.5 py-3 whitespace-nowrap">{usageText(promo)}</td>
                <td className="px-3.5 py-3 whitespace-nowrap">{expiryText(promo)}</td>
                <td className="px-3.5 py-3">
                  <StatusBadge promo={promo} />
                </td>
                <td className="max-w-[200px] truncate px-3.5 py-3 text-ink-muted" title={promo.createdBy}>
                  {promo.createdBy}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>
    </>
  )
}
